import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface SimpleSummarySchoolResponse {
  simple_summary: {
    students: SimpleSummaryStudent[]
    school: SimpleSummarySchool
    teachers: SimpleSummaryTeacher[]
  }
}

export interface SimpleSummaryStudent {
  student_id: number
  total_time: number
  lessons_completed: Nullable<number>
  lexile_growth?: Nullable<number>
}

export interface SimpleSummarySchool {
  total_time: number
  student_count: number
  lessons_completed: number
  average_lessons_completed_per_student: Nullable<number>
  average_total_time_per_student: Nullable<number>
  average_lessons_completed_per_teacher: Nullable<number>
  average_total_time_per_teacher: Nullable<number>
  average_lexile_growth?: Nullable<number>
}

export interface SimpleSummaryTeacher {
  teacher_id: string
  teacher_student_count: number
  contributing_student_count: number
  total_time: number
  lessons_completed: number
  average_lessons_completed_per_student: Nullable<number>
  average_total_time_per_student: Nullable<number>
  average_lexile_growth?: Nullable<number>
}

export async function simpleSummarySchool(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs,
  statsScope: SchoolScope,
  period: NormalizedInterval,
  teachers: {
    teachers: {
      teacher_id: string
      student_ids: string[]
    }[]
  },
): Promise<Nullable<SimpleSummarySchoolResponse['simple_summary']>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('simple_summary', product, statsScope)
  const query = this.buildQuery(statsScope, period, teachers)

  const data = await this.fetchData<SimpleSummarySchoolResponse>(url, query)
  if (!data) return null

  const { simple_summary } = data
  if (simple_summary.students.length === 0 || simple_summary.teachers.length === 0) return null

  return simple_summary
}

export interface SimpleSummaryStudentsResponse {
  simple_summary: {
    students: SimpleSummaryStudent[]
    totals: SimpleSummaryTotals
  }
}

export interface SimpleSummaryTotals {
  total_time: number
  student_count: number
  lessons_completed: number
  average_lessons_completed_per_student: Nullable<number>
  average_total_time_per_student: Nullable<number>
  average_lexile_growth?: Nullable<number>
}

export async function simpleSummaryStudents(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs,
  statsScope: StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<SimpleSummaryStudentsResponse['simple_summary']>> {
  if (isEmpty(statsScope.ids)) return null
  const url = this.buildUrl('simple_summary', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<SimpleSummaryStudentsResponse>(url, query)
  if (!data) return null

  const { simple_summary } = data
  if (simple_summary.students.length === 0) return null

  return simple_summary
}
